import React from 'react';
import { motion } from 'framer-motion';
import { Clock, ShieldCheck, HelpCircle } from 'lucide-react';
import PricingCard from '../components/PricingCard';
import { pricingData } from '../data/pricing';
import { GlassCard } from '../components/GlassCard';

const Pricing = ({ lang = 'en' }) => {
  const isAr = lang === 'ar';

  const faqContent = [
    {
      q: isAr
        ? 'هل الأسعار تشمل الأدوات الطبية؟'
        : 'Do prices include medical supplies?',
      a: isAr
        ? 'الأسعار تشمل خدمات الرعاية والتمريض فقط. الأدوات الطبية والمستهلكات يتم توفيرها من قبل العميل.'
        : 'Prices cover care and nursing services only. Medical supplies and consumables are provided by the client.',
    },
    {
      q: isAr
        ? 'كيف يتم اختيار مقدمي الرعاية؟'
        : 'How are caregivers selected?',
      a: isAr
        ? 'جميع طاقمنا مرخصين ومدربين مهنياً ويخضعون لفحوصات دقيقة لضمان أمان عائلتكم.'
        : 'All our staff are licensed, professionally trained, and undergo rigorous background checks to ensure family safety.',
    },
  ];

  return (
    <div
      className='pt-24 pb-20 bg-[#050505] min-h-screen'
      dir={isAr ? 'rtl' : 'ltr'}
    >
      {/* 1. Header Section */}
      <section className='section-padding py-16 text-center'>
        <div className='max-w-7xl mx-auto'>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className='text-4xl md:text-6xl font-black text-white mb-6'>
              {isAr ? 'خطط أسعار شفافة' : 'Transparent Pricing Plans'}
            </h1>
            <p className='text-lg text-gray-400 max-w-2xl mx-auto font-medium leading-relaxed'>
              {isAr
                ? 'نقدم خيارات رعاية مرنة تناسب احتياجاتكم، من الزيارات اليومية إلى الرعاية المقيمة على مدار الساعة.'
                : 'Flexible care options tailored to your needs, ranging from daily visits to 24/7 live-in professional care.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* 2. 12-Hour Duty Section */}
      <section className='section-padding mb-24'>
        <div className='max-w-7xl mx-auto'>
          <div
            className={`flex items-center gap-4 mb-12 border-b border-white/10 pb-6 ${isAr ? 'flex-row' : 'flex-row'}`}
          >
            <div className='p-4 bg-medical-blue-600 text-white rounded-2xl shadow-lg shadow-medical-blue-600/20'>
              <Clock size={32} />
            </div>
            <div className={isAr ? 'text-right' : 'text-left'}>
              <h2 className='text-3xl font-black text-white'>
                {isAr ? 'دوام ١٢ ساعة' : '12 Hours Duty Cycle'}
              </h2>
              <p className='text-sm font-bold text-medical-blue-400 uppercase tracking-widest mt-1'>
                {isAr ? 'رعاية نهارية أو ليلية' : 'Day or Night Care'}
              </p>
            </div>
          </div>

          <div className='grid md:grid-cols-2 lg:grid-cols-4 gap-6'>
            {pricingData.shift12.map((plan, index) => (
              <PricingCard
                key={plan.id}
                lang={lang}
                title={
                  isAr ? `باقة ${plan.duration.ar}` : `${plan.duration.en} Plan`
                }
                price={plan.price}
                duration={plan.duration[lang]}
                features={plan.features[lang]}
                isPopular={plan.isPopular}
                delay={index * 0.1}
                isDark={true}
              />
            ))}
          </div>
        </div>
      </section>

      {/* 3. 24-Hour Duty Section */}
      <section className='section-padding mb-24'>
        <div className='max-w-7xl mx-auto'>
          <div className='flex items-center gap-4 mb-12 border-b border-white/10 pb-6'>
            <div className='p-4 bg-trust-teal text-white rounded-2xl shadow-lg shadow-trust-teal/20'>
              <ShieldCheck size={32} />
            </div>
            <div className={isAr ? 'text-right' : 'text-left'}>
              <h2 className='text-3xl font-black text-white'>
                {isAr ? 'دوام ٢٤ ساعة' : '24 Hours Duty Cycle'}
              </h2>
              <p className='text-sm font-bold text-trust-teal uppercase tracking-widest mt-1'>
                {isAr ? 'رعاية مقيمة كاملة' : 'Full Live-in Care'}
              </p>
            </div>
          </div>

          <div className='grid md:grid-cols-2 lg:grid-cols-4 gap-6'>
            {pricingData.shift24.map((plan, index) => (
              <PricingCard
                key={plan.id}
                lang={lang}
                title={
                  isAr ? `باقة ${plan.duration.ar}` : `${plan.duration.en} Plan`
                }
                price={plan.price}
                duration={plan.duration[lang]}
                features={plan.features[lang]}
                isPopular={plan.isPopular}
                delay={index * 0.1}
                isDark={true}
              />
            ))}
          </div>
        </div>
      </section>

      {/* 4. FAQ / Fine Print Section */}
      <section className='section-padding pb-24'>
        <div className='max-w-4xl mx-auto'>
          <GlassCard className='p-8 md:p-12 border-white/10 bg-white/[0.02] shadow-2xl'>
            <div
              className={`flex items-center gap-4 mb-10 ${isAr ? 'flex-row' : 'flex-row'}`}
            >
              <HelpCircle className='text-medical-blue-400' size={40} />
              <h3 className='text-2xl md:text-3xl font-black text-white'>
                {isAr ? 'الأسئلة الشائعة' : 'Frequently Asked Questions'}
              </h3>
            </div>

            <div className='space-y-10'>
              {faqContent.map((faq, i) => (
                <div key={i} className={isAr ? 'text-right' : 'text-left'}>
                  <p className='text-lg font-black text-medical-blue-400 mb-3'>
                    {faq.q}
                  </p>
                  <p className='text-gray-400 font-medium leading-relaxed text-base'>
                    {faq.a}
                  </p>
                </div>
              ))}
            </div>

            <div className='mt-12 p-6 bg-white/[0.03] rounded-2xl border border-white/10 text-center'>
              <p className='text-sm font-bold text-gray-500 italic'>
                {isAr
                  ? 'ملاحظة: جميع الأسعار قابلة للتغيير بناءً على الحالة الصحية والاحتياجات الطبية الخاصة.'
                  : 'Note: All prices are subject to change based on the specific medical condition and health requirements.'}
              </p>
            </div>
          </GlassCard>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
